/* eslint-disable @typescript-eslint/no-explicit-any */
import type Highcharts from "highcharts";
import debounce from "lodash.debounce";

// careful, used in css files, no aliasing!!!
const MULTIPLE_SELECTION_CLASS = "multiple-selection-tooltip";
const STICKY_CLASS = "sticky-tooltip";
const INTERACTIVE_CLASS = "interactive-tooltip";

// Sticky tooltip. Tooltip automatically get a STICKY_CLASS class attached
// when the point they're attached to is selected and won't hide on mouse point leave.

declare module "highcharts" {
  interface Tooltip {
    point?: Highcharts.Point;
  }
  interface TooltipOptions {
    sticky?: boolean;
  }

  interface SeriesOptions {
    isInternal?: boolean; // navigator
  }
}

export function getMainAxisSelectedPoints(chart: Highcharts.Chart) {
  const selectedPoints = chart.getSelectedPoints();
  const internalPoints = selectedPoints.filter(p => p.series.userOptions.isInternal);
  if (internalPoints.length) {
    // eslint-disable-next-line no-console
    console.error("These points should not be selected (internal => navigator)", {
      internalPoints,
      selectedPoints,
      "selectedPoints.filter(p => !p.series.userOptions.isInternal)": selectedPoints.filter(
        p => !p.series.userOptions.isInternal,
      ),
    });
  }
  return selectedPoints.filter(p => !p.series.userOptions.isInternal);
}

const refreshTooltip = debounce(
  function (this: Highcharts.Chart, delay = 0) {
    window.setTimeout(() => {
      const selectedPoints = getMainAxisSelectedPoints(this);
      const lastSelectedPoint = selectedPoints[selectedPoints.length - 1];
      if (lastSelectedPoint) this.tooltip.refresh(lastSelectedPoint);
    }, delay);
  },
  100,
  { maxWait: 100, trailing: true, leading: false },
);

export default function stickyTooltips(H: typeof Highcharts) {
  // fix position when plot area moves/window resizes
  H.addEvent(H.Chart, "load", function (this: Highcharts.Chart) {
    if (!this.options.tooltip?.sticky) return;
    if (!this.options.tooltip?.outside) throw new Error("Sticky tooltip must use outside option");

    const handleSetExtremes = refreshTooltip.bind(this);
    const handleSetExtremesDelayed = refreshTooltip.bind(this, 200);

    H.addEvent(this.xAxis[0], "afterSetExtremes", handleSetExtremes);

    for (const yAxis of this.yAxis) {
      H.addEvent(yAxis, "afterSetExtremes", handleSetExtremes);
    }

    H.addEvent(this.container, "keydown", (e: KeyboardEvent) => {
      switch (e.key) {
        case "Escape": {
          const points = this.getSelectedPoints();
          if (!points.length) return;
          for (const p of points) p.select(false);
          break;
        }
        default:
          return;
      }
      e.preventDefault();
    });

    window.addEventListener("resize", handleSetExtremesDelayed);
    H.addEvent(this, "destroy", () => {
      window.removeEventListener("resize", handleSetExtremesDelayed);
    });
  });

  // do not hide tooltips when point is selected, or move it to last selected point
  H.wrap(
    H.Tooltip.prototype,
    "hide",
    function (
      this: Highcharts.Tooltip,
      proceed: { apply: (arg0: Highcharts.Tooltip, arg1: [Highcharts.Options]) => never },
      ...args: [Highcharts.Options]
    ) {
      if (!this.options.sticky) return proceed.apply(this, args);
      const selectedPoints = getMainAxisSelectedPoints(this.chart);
      const lastSelectedPoint = selectedPoints[selectedPoints.length - 1];
      if (!lastSelectedPoint) return proceed.apply(this, args); // if no selected points use default behavior
      if (this.point?.selected) return; // do not hide, we're fine here
      this.point = lastSelectedPoint;
      this.refresh(this.point); // move to last selected point
      return;
    },
  );

  // put point on tooltip for "hide", add a sticky class to container
  // and change tooltip borderColor to gray if point is selectedand there are two ore more points selected
  H.wrap(
    H.Tooltip.prototype,
    "refresh",
    function (
      this: Highcharts.Tooltip,
      proceed: { apply: (arg0: Highcharts.Tooltip, arg1: unknown[]) => void },
      ...args: (Highcharts.Point | undefined)[]
    ) {
      const container = this.container;
      if (!this.options.sticky) return proceed.apply(this, args);
      this.point = args[0];
      const selectedPoints = getMainAxisSelectedPoints(this.chart);
      if (!this.point || !container) return proceed.apply(this, args);

      if (this.point.selected && selectedPoints.length > 1) {
        container.classList.add(MULTIPLE_SELECTION_CLASS);
      } else {
        container.classList.remove(MULTIPLE_SELECTION_CLASS);
      }

      if (this.point.selected) {
        container.classList.add(STICKY_CLASS);
        // wait for animations to be done before we add pointer-events (avoid click hijacking when double-clicking the upper part of points)
        window.setTimeout(() => {
          if (this.point?.selected) {
            container.classList.add(INTERACTIVE_CLASS);
          } else container.classList.remove(INTERACTIVE_CLASS);
        }, 400);
      } else {
        container.classList.remove(STICKY_CLASS);
        container.classList.remove(INTERACTIVE_CLASS);
      }

      return proceed.apply(this, args);
    },
  );

  // fix updatePosition when tooltip leaves plot area when options.outside.
  // not done for outside: false, we don't need it
  H.wrap(
    H.Tooltip.prototype,
    "updatePosition",
    function (
      this: Highcharts.Tooltip,
      proceed: { apply: (arg0: Highcharts.Tooltip, arg1: unknown[]) => unknown },
      ...args: { plotY: number; plotX: number }[]
    ) {
      if (!this.chart.options.tooltip?.sticky || !this.chart.options.tooltip?.outside)
        return proceed.apply(this, args);
      const xAxis = this.chart.xAxis[0];
      if (!xAxis) throw new Error("Missing xAxis");
      const yAxis = this.chart.yAxis[0];
      if (!yAxis) throw new Error("Missing yAxis");

      if (!this.renderer) throw new Error("renderer missing?");
      if (!this.point) throw new Error("Point missing?");

      // let Highcharts handle tooltip position if point is between userMin and userMax
      const { x: pointPositionX, y: pointPositionY } = this.point;
      const { userMin: userMinX, userMax: userMaxX } = xAxis.getExtremes();
      // wrong types: yAxis.getExtremes().userMin/Max can be number undefined or null
      const { userMin: userMinY, userMax: userMaxY } = <
        { userMin: number | undefined | null; userMax: number | undefined | null }
      >yAxis.getExtremes();

      if (
        pointPositionX >= userMinX &&
        pointPositionX <= userMaxX &&
        pointPositionY !== undefined &&
        (userMinY === undefined || userMinY === null || pointPositionY >= userMinY) &&
        (userMaxY === undefined || userMaxY === null || pointPositionY <= userMaxY)
      )
        return proceed.apply(this, args);

      const chartPosition = this.chart.pointer.getChartPosition();
      const leftEdgeX = chartPosition.left + this.chart.plotLeft;
      const rightEdgeX = leftEdgeX + this.chart.plotWidth;
      const topEdgeY = chartPosition.top + this.chart.plotTop;
      const bottomEdgeY = topEdgeY + this.chart.plotHeight;

      // for some reason args[0].plotX === Nan when selected point is now out of the pane (Highcharts 8.2.2)
      const plotX = xAxis.toPixels(this.point.x, true);
      // but args[0].plotY is fine, go figure

      const plotY = args[0]?.plotY;
      if (plotY === undefined) throw new Error("Missing plotY");

      const pointX = plotX + leftEdgeX;
      const pointY = plotY + topEdgeY;

      const { width, height } = this.getLabel() as any;
      const padding = (this.options.borderWidth ?? 0) + (this as any).distance;
      this.renderer.setSize(width + padding * 2, height + padding * 2, false);

      let x;
      let y;

      if (pointX < leftEdgeX) {
        // point is on the left of viewport, position tooltip to the right of the edge
        x = leftEdgeX - padding;
      } else if (pointX > rightEdgeX) {
        // point is on the right of viewport , position tooltip to the left of the edge
        x = rightEdgeX - width - padding;
      } else {
        // else center tooltip horizontally on the point
        x = pointX - width / 2 - padding;
      }

      if (pointY < topEdgeY) {
        // point is over viewport, position tooltip to the bottom of the edge
        y = topEdgeY - padding;
      } else if (pointY > bottomEdgeY) {
        // point is under viewport, position tooltip to the top of the edge
        y = bottomEdgeY - height - padding;
      } else {
        if (pointX < leftEdgeX || pointX > rightEdgeX) {
          // point is outside of viewport on X axis, center it vertically on the point
          y = pointY - height / 2 - padding;
        } else {
          // point is inside viewport vertically, position tooltip to the top of the point
          y = pointY - height - padding * 2;
        }
      }

      (this as any).move(x, y, pointX - x, pointY - y);

      return;
    },
  );

  // we don't hide tooltips when they are out of the viewport. Let's pray they don't use the function for anything else than viewport check.
  // https://github.com/highcharts/highcharts/blob/c1086b5d6c710328d9ac9da4270b95cb25e5c56e/ts/Core/Series/Series.ts#L7285
  H.wrap(H.Series.prototype, "shouldShowTooltip", () => true);
}
